import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { IdCard, MapPin, AlertTriangle, Clock } from "lucide-react";
import { STATUS_CLS, fmtMins } from "@/components/attendanceUi";
import HrTabs from "./HrTabs";

const tm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—");
const day = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });

/* HR → Attendance: the GPS attendance the ERP recorded for the HR app's own
   Check in / Check out (HR App tab) — today and the last 30 days
   (backend/hr_attendance.py). Checking in happens only in the HR app. */
export default function Attendance() {
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/hr/attendance/me").then(({ data }) => setMe(data))
      .catch((e) => setError(formatApiError(e?.response?.data?.detail) || e.message));
  }, []);
  const rec = me?.record;
  const hist = me?.history || [];
  return (
    <div className="space-y-4" data-testid="hr-attendance">
      <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
        <IdCard className="w-5 h-5 text-blue-600" /> HR
      </h1>
      <HrTabs />
      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
      {me && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-5 space-y-3" data-testid="attendance-today">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 font-semibold text-slate-900" style={{ fontFamily: "Manrope" }}>
                  <Clock className="w-4 h-4 text-blue-600" /> Today · {me.type_label}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Office hours {me.office.start}–{me.office.end} · check out by {tm(me.clockout_deadline)}</div>
              </div>
              {rec && <span className={`text-xs rounded-full border px-2 py-0.5 ${STATUS_CLS[rec.status]}`} data-testid="attendance-status">{rec.label}</span>}
            </div>
            {rec ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Check in</div><div className="font-mono">{tm(rec.clock_in?.at)}</div>
                  {rec.clock_in && <div className="text-xs text-slate-500 truncate" title={rec.clock_in.location}><MapPin className="w-3 h-3 inline mr-0.5" />{rec.clock_in.location}</div>}</div>
                <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Check out</div><div className="font-mono">{tm(rec.clock_out?.at)}</div>
                  {rec.clock_out && <div className="text-xs text-slate-500 truncate" title={rec.clock_out.location}><MapPin className="w-3 h-3 inline mr-0.5" />{rec.clock_out.location}</div>}
                  {rec.status === "present" && rec.worked_min != null && <div className="text-xs text-green-700">{fmtMins(rec.worked_min)} worked</div>}</div>
              </div>
            ) : me.attendance_type !== "exempt" && <p className="text-sm text-slate-600">Not checked in yet today.</p>}
            {rec?.status === "absent_no_clockout" && (
              <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 flex gap-2" data-testid="attendance-absent">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>You didn't check out by {tm(me.clockout_deadline)}, so today isn't counted — it's marked <b>absent</b>. If you worked, ask HR to correct it.</span>
              </div>
            )}
            {rec?.status === "working" && (
              <p className="text-xs text-amber-700 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Check out before {tm(me.clockout_deadline)}. Without a check-out after office hours, today's attendance is not counted and it's marked as an absence.</p>
            )}
            {me.attendance_type !== "exempt" && (
              <p className="text-sm">Check in and out with the <Link to="/hr" className="text-blue-700 font-medium hover:underline">HR App</Link> tab's <b>Check in / Check out</b> button — allow location when the browser asks.</p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 space-y-1.5">
            <div className="font-semibold text-slate-900">How attendance works</div>
            <p><b>Office</b> staff check in only within {me.office.radius_m} m of {me.office.name.split(",")[0]}.</p>
            <p><b>Field</b> staff (e.g. Marketing) check in from where they work; their live GPS location is saved.</p>
            <p><b>Work from home</b>: the GPS location is saved at check-in and check-out.</p>
            <p>Keep GPS on and allow location for canaresai.com. A day counts only after you <b>check out</b> — by
              {" "}{me.office.end} + {me.office.grace_min} min. Without a check-out, the day is marked <b>absent</b>.</p>
          </div>
        </div>
      )}

      {me && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <div className="px-4 py-3 font-semibold text-slate-900">My last 30 days</div>
          <table className="w-full text-sm" data-testid="attendance-history">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">In</th>
                <th className="text-left px-3 py-2">Out</th><th className="text-left px-3 py-2">Worked</th><th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Location (in)</th></tr>
            </thead>
            <tbody>
              {hist.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No attendance yet.</td></tr>}
              {hist.map((r) => (
                <tr key={r.date} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap">{day(r.date)}</td>
                  <td className="px-3 py-2">{r.type_label}</td>
                  <td className="px-3 py-2 font-mono">{tm(r.clock_in?.at)}</td>
                  <td className="px-3 py-2 font-mono">{tm(r.clock_out?.at)}{r.clock_out?.corrected_by && <span className="ml-1 text-[10px] text-amber-700" title={r.clock_out.reason}>(HR)</span>}</td>
                  <td className="px-3 py-2">{r.status === "present" ? fmtMins(r.worked_min) : "—"}</td>
                  <td className="px-3 py-2"><span className={`text-xs rounded-full border px-2 py-0.5 whitespace-nowrap ${STATUS_CLS[r.status]}`}>{r.label}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[280px] truncate" title={r.clock_in?.location}>
                    {r.clock_in && <><MapPin className="w-3 h-3 inline mr-0.5" />{r.clock_in.location}</>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
